import DataLoader from 'dataloader';
import { useApolloClient, ApolloClient } from '@apollo/client';
import { useMemo, useEffect } from 'react';
import {
  SearchCandidateProfileListsByIdsDocument,
  SearchCandidateProfileListsByIdsQuery
} from 'generated-graphql/graphql';
import { useOrganization } from 'core/stores/hooks/useOrganization';

type TalentListItem = NonNullable<
  NonNullable<
    SearchCandidateProfileListsByIdsQuery['searchCandidateProfileListsForUser']
  >['items'][number]
>;

// A list the user can't see resolves to null, so its name is never shown.
const createLoader = (
  apolloClient: ApolloClient<object>,
  organizationId: string
) =>
  new DataLoader<string, TalentListItem | null>(
    async (ids: readonly string[]) => {
      const { data } = await apolloClient.query({
        query: SearchCandidateProfileListsByIdsDocument,
        variables: {
          organizationId,
          filter: JSON.stringify({
            or: ids.map((id) => ({ id: { eq: id } }))
          }),
          limit: ids.length
        }
      });

      const items = data?.searchCandidateProfileListsForUser?.items ?? [];
      const map = new Map(
        items
          .filter((item): item is TalentListItem => item !== null)
          .map((item) => [item.id, item])
      );

      return ids.map((id) => map.get(id) || null);
    },
    { cache: true }
  );

export const useTalentListDataLoader = () => {
  const apolloClient = useApolloClient();
  const organization = useOrganization();
  const organizationId = organization.id;

  // Pure useMemo - just create and return
  const loader = useMemo(() => {
    return createLoader(apolloClient, organizationId);
  }, [apolloClient, organizationId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      loader.clearAll();
    };
  }, [loader]);

  return loader;
};
